<script setup lang="ts">
import { computed, ref } from 'vue'

import type { AdjustConfig } from '../../models/definition.types'
import { isValidUtcOffset } from '../../service/adjustConfig'

import type { useWorkspaceAttribute } from '@/features/workspace-settings'
import { useI18n } from '@/shared/i18n'
import { Alert, Button, Input, Modal, Select, type SelectOption, useToast } from '@/shared/ui'

const props = defineProps<{
  attribute: ReturnType<typeof useWorkspaceAttribute<'adjust_configurations'>>
}>()
const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()
const toast = useToast()
const open = ref(true)
const saved = props.attribute.value.value
const draft = ref<AdjustConfig>({
  attribution_type: saved?.attribution_type ?? 'click',
  attribution_source: saved?.attribution_source ?? 'dynamic',
  reattributed: saved?.reattributed ?? 'all',
  utc_offset: saved?.utc_offset ?? '',
})
const utcValid = computed(() => isValidUtcOffset(draft.value.utc_offset))
const options = <T extends string>(values: readonly T[]): SelectOption<T>[] =>
  values.map((value) => ({ value, label: t(`workspaceDefinitions.adjust.options.${value}`) }))
const typeOptions = options(['click', 'impression', 'all'] as const)
const sourceOptions = options(['dynamic', 'first'] as const)
const reattributedOptions = options(['all', 'false', 'true'] as const)

async function save(): Promise<void> {
  if (!utcValid.value) return
  const result = await props.attribute.save({ ...draft.value })
  if (!result) return
  toast.success(t('workspaceDefinitions.adjust.saved'))
  emit('close')
}
</script>

<template>
  <Modal
    v-model="open"
    :title="t('workspaceDefinitions.adjust.title')"
    size="md"
    @close="emit('close')"
  >
    <template #body>
      <div class="adjust-config-modal__body grid gap-md">
        <Select
          v-model="draft.attribution_type"
          :options="typeOptions"
          :label="t('workspaceDefinitions.adjust.fields.attributionType')"
        />
        <Select
          v-model="draft.attribution_source"
          :options="sourceOptions"
          :label="t('workspaceDefinitions.adjust.fields.attributionSource')"
        />
        <Select
          v-model="draft.reattributed"
          :options="reattributedOptions"
          :label="t('workspaceDefinitions.adjust.fields.reattributed')"
        />
        <Input
          v-model="draft.utc_offset"
          :label="t('workspaceDefinitions.adjust.fields.utcOffset')"
          :placeholder="t('workspaceDefinitions.adjust.utcPlaceholder')"
          :error="draft.utc_offset.length > 0 && !utcValid"
        />
        <p
          v-if="draft.utc_offset && !utcValid"
          class="adjust-config-modal__error m-0 text-danger text-[0.75rem]"
        >
          {{ t('workspaceDefinitions.adjust.utcError') }}
        </p>
        <Alert v-if="attribute.error.value" tone="danger">{{
          t('workspaceDefinitions.common.saveError')
        }}</Alert>
      </div>
    </template>
    <template #buttons>
      <div class="adjust-config-modal__actions flex justify-end gap-sm">
        <Button
          variant="outline"
          size="sm"
          :disabled="attribute.pending.value"
          @click="emit('close')"
          >{{ t('workspaceDefinitions.common.cancel') }}</Button
        >
        <Button size="sm" :disabled="!utcValid" :loading="attribute.pending.value" @click="save">{{
          t('workspaceDefinitions.common.set')
        }}</Button>
      </div>
    </template>
  </Modal>
</template>
